import React, { useState, useRef } from 'react';
import classnames from 'classnames';
import GlobalProps from '../../globalProps';
import { useForm } from '../Form';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import './TextArea.css';
import './../input.scss';
import { t } from '../../../locales/translate';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface Props extends GlobalProps {
  name: string;
  label?: string;
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  description?: string;
  placeholder?: string;
  errorMessage?: string;
  json?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  readOnly?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: (value: string) => void;
  onBlur?: (value: string) => void;
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  monospace?: boolean;
}

export function TextArea({
  name,
  label,
  icon,
  actions,
  description,
  placeholder,
  errorMessage,
  json = false,
  autoFocus,
  required,
  disabled,
  maxLength,
  readOnly,
  className,
  value,
  onChange,
  textareaRef,
  onFocus,
  onBlur,
  monospace,
}: Props): JSX.Element {
  const [contextValue, setValue, triedToSubmit, reportValidity] = useForm<string>(name);
  const [active, setActive] = useState(false);
  const [touched, setTouched] = useState(false);
  value = value !== undefined ? value : contextValue;

  const inputRef: React.MutableRefObject<HTMLTextAreaElement | null> = useRef(null);

  let validationMessage =
    errorMessage || ((triedToSubmit || touched) && inputRef.current && inputRef.current.validationMessage) || undefined;

  if (json && touched) {
    try {
      JSON.parse(value || '');
      reportValidity(true);
    } catch (e) {
      if (value) {
        reportValidity(false);
        validationMessage = `${t.errors.notValidJSON} (${(e as Error).message})`;
      }
    }
  }

  return (
    <div
      className={classnames(
        'input',
        'textarea',
        { 'textarea--with-icon': icon },
        { 'textarea--disabled': disabled },
        { 'textarea--monospace': monospace },
        className
      )}
    >
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': active || value || placeholder,
          'input__container--error': validationMessage,
        })}
      >
        {label && (
          <label className="input__label" htmlFor={name}>
            {label}
            {!required && ` (${t.general.optional})`}
          </label>
        )}
        <div className="input__input-wrapper">
          <div className="textarea__input-wrapper" data-replicated-value={value}>
            <textarea
              id={name}
              name={name}
              className="input__input"
              value={value || ''}
              onChange={(v) => {
                if (readOnly) {
                  v.preventDefault();
                  return;
                }
                if (onChange) {
                  onChange(v.target.value);
                } else {
                  setValue(v.target.value);
                }
              }}
              onFocus={() => {
                setActive(true);
                if (onFocus) {
                  onFocus(value || '');
                }
              }}
              onBlur={() => {
                setActive(false);
                if (value !== '') {
                  setTimeout(() => setTouched(true), 100);
                }
                if (onBlur) {
                  onBlur(value || '');
                }
              }}
              autoFocus={autoFocus}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              maxLength={maxLength}
              ref={(ref) => {
                inputRef.current = ref;
                if (textareaRef) {
                  textareaRef.current = ref;
                }
              }}
              rows={1}
            />
          </div>
          {actions && <div className="input__actions">{actions}</div>}
          {validationMessage && <Icon name="alert-circle--sm" className="input__alert-circle" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={validationMessage ? true : false}>
            <div className="input__error-message">{validationMessage}</div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
